import { ImagePlus, Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";

export const TextField = ({ label, value, onChange, placeholder, testid, multiline }) => (
  <label className="field">
    <span className="field-label">{label}</span>
    {multiline ? (
      <textarea rows={3} className="field-input !h-auto py-3" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    ) : (
      <input className="field-input" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    )}
  </label>
);

export const ColorField = ({ label, value, onChange, testid }) => (
  <label className="field">
    <span className="field-label">{label}</span>
    <div className="flex items-center gap-3 rounded-xl border border-ink/10 bg-white p-2">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} className="h-9 w-12 cursor-pointer rounded-md border-0 bg-transparent" data-testid={`${testid}-picker`} />
      <input className="w-full bg-transparent font-mono text-sm uppercase outline-none" value={value} maxLength={7}
        onChange={(e) => /^#[0-9a-fA-F]{0,6}$/.test(e.target.value) && onChange(e.target.value.toUpperCase())} data-testid={testid} />
    </div>
  </label>
);

export const ImageField = ({ label, value, onChange, placeholder, placeholderLabel = "Using reservation system image", testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
      ref.current.value = "";
    }
  };
  const shown = value || placeholder;
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <div className="flex gap-3">
        <div className="h-20 w-28 shrink-0 overflow-hidden rounded-xl border border-ink/10 bg-linen">
          {shown && <img src={shown} alt="" className={`h-full w-full object-cover ${value ? "" : "opacity-50"}`} data-testid={`${testid}-preview`} />}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <input className="field-input !h-10" value={value} placeholder={placeholder ? placeholderLabel : "Image URL"}
            onChange={(e) => onChange(e.target.value)} data-testid={testid} />
          <div>
            <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`${testid}-file`} />
            <button type="button" className="btn-ghost" disabled={busy} onClick={() => ref.current.click()} data-testid={`${testid}-upload`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}Upload image
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Panel = ({ title, hint, children, action }) => (
  <section className="rounded-2xl border border-ink/10 bg-white p-6">
    <div className="mb-5 flex items-start justify-between gap-4">
      <div>
        <h2 className="font-serif text-xl">{title}</h2>
        {hint && <p className="mt-1 text-sm text-ink/55">{hint}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);
